import { useRef, useEffect, useState, useCallback } from "react";
import Globe from "react-globe.gl";

const SERVER_CONNECTIONS = [
  { startLat: 37.77, startLng: -122.42, endLat: 35.68, endLng: 139.65 },
  { startLat: 40.71, startLng: -74.01, endLat: 51.51, endLng: -0.13 },
  { startLat: 51.51, startLng: -0.13, endLat: 1.35, endLng: 103.82 },
  { startLat: 35.68, startLng: 139.65, endLat: 1.35, endLng: 103.82 },
  { startLat: 1.35, startLng: 103.82, endLat: -33.87, endLng: 151.21 },
  { startLat: -23.55, startLng: -46.63, endLat: 40.71, endLng: -74.01 },
  { startLat: 28.61, startLng: 77.21, endLat: 1.35, endLng: 103.82 },
  { startLat: 55.75, startLng: 37.62, endLat: 35.68, endLng: 139.65 },
];

const quickLinks = [
  { label: "How do I get started with Helocore?", href: "#cta" },
  { label: "What makes Helocore Cloud different?", href: "#products" },
  { label: "Explore Helocore Studio features", href: "#products" },
  { label: "See our global infrastructure", href: "#features" },
];

const newsCards = [
  {
    tag: "Product",
    title: "Helocore Studio is in Alpha — closed Beta launches June 1, 2026.",
    color: "#6C5CE7",
  },
  {
    tag: "Infrastructure",
    title: "300+ cities at launch. Sub-50ms global latency. Built for speed.",
    color: "#34a853",
  },
  {
    tag: "Hosting",
    title: "Helocore Cloud — launching in closed Alpha, November 2026.",
    color: "#ffc366",
  },
];

const HeroSection = ({ onOpenWaitlist }) => {
  const globeRef = useRef();
  const [countries, setCountries] = useState({ features: [] });

  useEffect(() => {
    fetch("https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson")
      .then((r) => r.json())
      .then((d) => setCountries(d))
      .catch(() => {});
  }, []);

  const handleGlobeReady = useCallback(() => {
    if (globeRef.current) {
      const c = globeRef.current.controls();
      c.autoRotate = true;
      c.autoRotateSpeed = 0.4;
      c.enableZoom = false;
      c.enablePan = false;
      c.enableRotate = false;
      globeRef.current.pointOfView({ lat: 25, lng: 10, altitude: 2.0 });
    }
  }, []);

  return (
    <section className="pt-[94px] bg-white" data-testid="hero-section">
      {/* Main hero */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-4 items-center min-h-[calc(100vh-200px)] py-12 lg:py-0">
          {/* Left — text content */}
          <div className="max-w-xl">
            <h1
              className="text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.15] font-normal text-[#202124] tracking-[-0.02em]"
              data-testid="hero-title"
            >
              Build what's next{" "}
              <span className="text-[#6C5CE7]">with Helocore</span>
            </h1>

            <p
              className="mt-5 text-[15px] sm:text-base lg:text-lg text-[#5f6368] leading-relaxed max-w-lg"
              data-testid="hero-subtitle"
            >
              From your first line of code to running it around the world, Helocore makes it simple and seamless, and introduces an internet you can be part of and earn from.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={onOpenWaitlist}
                className="inline-flex items-center px-6 py-3 bg-[#6C5CE7] hover:bg-[#e8872e] text-white text-[14px] font-medium rounded-md transition-colors shadow-sm"
                data-testid="hero-get-started-button"
              >
                Join waitlist
              </button>
              <button
                onClick={onOpenWaitlist}
                className="inline-flex items-center px-6 py-3 border border-[#dadce0] hover:bg-[#f8f9fa] text-[14px] font-medium text-[#6C5CE7] rounded-md transition-colors"
                data-testid="hero-contact-button"
              >
                Contact us
              </button>
            </div>

            <div className="mt-6 flex items-center gap-5" data-testid="accelerator-badges">
              <a href="https://www.yorku.ca/yspace/" target="_blank" rel="noopener noreferrer" className="inline-flex">
                <img src="https://www.yorku.ca/yspace/wp-content/uploads/sites/231/2024/01/YSpace_Keele_2020_RGB.png" alt="YSpace" className="h-[20px] w-auto opacity-70 hover:opacity-100 transition-opacity" />
              </a>
              <a href="https://www.venturelab.ca" target="_blank" rel="noopener noreferrer" className="inline-flex">
                <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_helocore-core/artifacts/r2vkdh6a_image.webp" alt="ventureLAB" className="h-[32px] w-auto opacity-70 hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>

          {/* Right — Globe */}
          <div className="relative flex items-center justify-center lg:justify-end overflow-hidden" data-testid="hero-globe">
            <div className="w-[280px] h-[280px] sm:w-[400px] sm:h-[400px] lg:w-[520px] lg:h-[520px] relative">
              <Globe
                ref={globeRef}
                onGlobeReady={handleGlobeReady}
                width={520}
                height={520}
                backgroundColor="rgba(0,0,0,0)"
                showGlobe={true}
                globeImageUrl="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3Crect fill='%23f8f9fa' width='1' height='1'/%3E%3C/svg%3E"
                showAtmosphere={true}
                atmosphereColor="rgba(255, 154, 77, 0.08)"
                atmosphereAltitude={0.15}
                polygonsData={countries.features}
                polygonCapColor={() => "#6C5CE7"}
                polygonSideColor={() => "#ffc366"}
                polygonStrokeColor={() => "rgba(0,0,0,0.04)"}
                polygonAltitude={0.008}
                arcsData={SERVER_CONNECTIONS}
                arcColor={() => ["#ffc36680", "#6C5CE780"]}
                arcDashLength={0.4}
                arcDashGap={0.3}
                arcDashAnimateTime={4000}
                arcStroke={0.5}
                arcAltitudeAutoScale={0.4}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quick links bar — Google-style interactive prompt */}
      <div className="bg-[#f8f9fa] border-t border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-wrap gap-3" data-testid="hero-quick-links">
            {quickLinks.map((q) => (
              <a
                key={q.label}
                href={q.href}
                className="px-4 py-2.5 bg-white border border-[#dadce0] rounded-full text-[13px] text-[#5f6368] hover:text-[#202124] hover:border-[#6C5CE7] hover:shadow-sm transition-all"
                data-testid={`quick-link-${q.label.slice(0, 10).replace(/\s/g, "-").toLowerCase()}`}
              >
                {q.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* News cards — Google Cloud style */}
      <div className="bg-white border-t border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="hero-news-cards">
            {newsCards.map((card, i) => (
              <a
                key={i}
                href="#"
                className="group block p-6 bg-white border border-[#e8eaed] rounded-xl hover:shadow-md hover:border-[#dadce0] transition-all"
                data-testid={`news-card-${i}`}
              >
                <span
                  className="inline-block text-[11px] font-bold tracking-wide uppercase mb-3"
                  style={{ color: card.color }}
                >
                  {card.tag}
                </span>
                <p className="text-[15px] font-medium text-[#202124] leading-snug group-hover:text-[#6C5CE7] transition-colors">
                  {card.title}
                </p>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
